/**
 * TeamSystem Impact - Working Year Store
 * Global store for managing the working year across all modules
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface WorkingYearState {
  year: number;
  setYear: (year: number) => void;
  availableYears: number[];
  /**
   * `true` quando l'elenco degli anni e' quello confermato dal server, non
   * quello rimasto nel browser da una sessione precedente. Serve a chi deve
   * decidere in base agli anni esistenti (vedi `useYearFromUrl`): agire sulla
   * lista persistita significa agire su anni che potrebbero non esserci piu'.
   * Non si persiste: a ogni avvio si riparte da `false` finche' il server non
   * risponde.
   */
  yearsSynced: boolean;
  addYear: (year: number) => void;
  /**
   * Sostituisce l'intera lista degli anni disponibili con quella
   * autoritativa proveniente dal server (setup DMA dell'azienda).
   * A differenza di addYear (additivo), questa rimpiazza la lista:
   * elimina eventuali anni residui salvati solo nel browser locale.
   * Se l'anno di lavoro corrente non è più tra quelli disponibili,
   * viene riportato all'anno più recente.
   */
  setAvailableYears: (years: number[]) => void;
}

const currentYear = new Date().getFullYear();

export const useWorkingYearStore = create<WorkingYearState>()(
  persist(
    (set, get) => ({
      year: currentYear - 1,
      availableYears: [currentYear - 1, currentYear - 2, currentYear - 3],
      yearsSynced: false,
      setYear: (year: number) => set({ year }),
      addYear: (year: number) => {
        const current = get().availableYears;
        if (!current.includes(year)) {
          set({ availableYears: [...current, year].sort((a, b) => b - a) });
        }
      },
      setAvailableYears: (years: number[]) => {
        // Il flag si alza per primo: dice «il server ha risposto», non «la
        // risposta conteneva qualcosa». Alzandolo piu' in basso, una risposta
        // vuota lo lascerebbe falso per sempre e chi lo aspetta resterebbe
        // fermo.
        set({ yearsSynced: true });
        const sorted = [...new Set(years)].sort((a, b) => b - a);
        if (sorted.length === 0) {
          // Nessun anno sul server: lascio invariata la lista locale
          // per non svuotare il selettore (es. azienda appena creata).
          return;
        }
        const currentYear = get().year;
        const nextYear = sorted.includes(currentYear) ? currentYear : sorted[0];
        set({ availableYears: sorted, year: nextYear });
      },
    }),
    {
      name: 'teamsystem-impact-working-year',
      // Il flag di sincronizzazione NON si salva: dice «il server ha risposto in
      // QUESTA sessione», e ripescarlo dal browser lo renderebbe una bugia.
      partialize: (state) => ({ year: state.year, availableYears: state.availableYears }),
    }
  )
);

// Convenience hook
export const useWorkingYear = () => {
  const { year, setYear, availableYears, addYear, setAvailableYears } = useWorkingYearStore();
  return { year, setYear, availableYears, addYear, setAvailableYears };
};
